import { Routes, Route, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { apiFetch } from './lib/api';
import { useConsoleStore, type ConsoleUser, type ConsoleStore } from './store';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { Verify } from './pages/Verify';
import { StoreSelector } from './pages/StoreSelector';
import { MerchantWorkspace } from './pages/MerchantWorkspace';
import Home from './pages/Home';
import { InternalAdmin } from './pages/InternalAdmin';
import ActionLog from './pages/ActionLog';
import PromoCodes from './pages/PromoCodes';
import BetaAccessRequests from './pages/BetaAccessRequests';

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { setUser, setStores } = useConsoleStore();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<{ user: ConsoleUser; stores: ConsoleStore[] }>('/api/console/me'),
    retry: false,
    staleTime: 60_000,
  });
  useEffect(() => {
    if (data) { setUser(data.user); setStores(data.stores); }
    if (isError) setUser(null);
  }, [data, isError]);

  if (isLoading) return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-300 border-t-zinc-900" />
    </div>
  );
  if (isError || !data) {
    const domain = new URLSearchParams(window.location.search).get('domain');
    const loginPath = domain ? `/login?domain=${encodeURIComponent(domain)}` : '/login';
    return <Navigate to={loginPath} replace />;
  }
  return <>{children}</>;
}

function HomeRoute() {
  const { user } = useConsoleStore();
  const isInternal = ['support', 'staff', 'superadmin'].includes(user?.role || '');
  return isInternal ? <Home /> : <StoreSelector />;
}

function RequireInternal({ children }: { children: React.ReactNode }) {
  const { user } = useConsoleStore();
  const isInternal = ['support', 'staff', 'superadmin'].includes(user?.role || '');
  if (!isInternal) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/verify" element={<Verify />} />
      <Route element={<AuthGuard><Layout /></AuthGuard>}>
        <Route index element={<HomeRoute />} />
        <Route path="/stores" element={<StoreSelector />} />
        <Route path="/workspace" element={<MerchantWorkspace />} />
        <Route path="/subscription" element={<Navigate to="/workspace?tab=plans" replace />} />
        <Route path="/admin" element={<RequireInternal><InternalAdmin /></RequireInternal>} />
        <Route path="/internal" element={<RequireInternal><InternalAdmin /></RequireInternal>} />
        <Route path="/action-log" element={<RequireInternal><ActionLog /></RequireInternal>} />
        <Route path="/beta-requests" element={<RequireInternal><BetaAccessRequests /></RequireInternal>} />
        <Route path="/promo-codes" element={<RequireInternal><PromoCodes /></RequireInternal>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
